// src/pages/dashboard/Subscription.tsx
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next'; 
import { CheckCircle, CreditCard, ArrowUpRight, Download, AlertCircle, X, Loader2, Plus } from 'lucide-react';
import {
  getCurrentSubscription,
  getSubscriptionPlans,
  upgradeSubscription,
  renewSubscription,
  getBillingHistory,
  getMyExtensionHistory,
  getListingUsage,                            // ✅ add
  initiateAdditionalListingPurchase,                                    // ✅
  CurrentSubscriptionData,
  BillingRecord,
  SubscriptionPlan,
  ExtensionHistoryRecord, 
  ListingUsage,                          // ✅
} from "../../../services/subscription.service";
import { purchaseSubscription } from "../../../services/auth.service";   
import { submitPaywallForm } from "../../../utils/payzone"; 
import { Seo } from "../../components/Seo";

// ---------- Skeleton Component ----------
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function SubscriptionSkeleton() {
  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <Skeleton className="h-8 w-72 mb-2" />
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="rounded-2xl p-6 bg-[#EAF2FF] mb-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-9 w-44" />
          </div>
          <Skeleton className="h-8 w-24 rounded-xl" />
        </div>
        <div className="grid grid-cols-3 gap-4 mb-5">
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-white/70 rounded-xl p-4">
              <Skeleton className="h-8 w-12 mx-auto mb-2" />
              <Skeleton className="h-4 w-24 mx-auto" />
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <Skeleton className="flex-1 h-11 rounded-xl" />
          <Skeleton className="flex-1 h-11 rounded-xl" />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-6">
        <Skeleton className="h-6 w-36 mb-5" />
        {[1, 2].map((bar) => (
          <div key={bar} className="mb-5">
            <div className="flex justify-between mb-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="p-5 border-b border-[#E5E7EB]">
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {[1, 2, 3, 4].map((row) => (
            <div key={row} className="flex items-center gap-4 px-5 py-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-7 w-16 rounded-lg" />
              <Skeleton className="w-9 h-9 rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Main Component ----------
export function Subscription() {
  const { t } = useTranslation('auth'); 
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<CurrentSubscriptionData | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [billing, setBilling] = useState<BillingRecord[]>([]);
  const [extensions, setExtensions] = useState<ExtensionHistoryRecord[]>([]);   // ✅
  const [upgrading, setUpgrading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [listingUsage, setListingUsage] = useState<ListingUsage | null>(null);
const [showBuyListingsModal, setShowBuyListingsModal] = useState(false);
const [buyListingsQty, setBuyListingsQty] = useState(1);
const [buyListingsDuration, setBuyListingsDuration] = useState<'Week' | 'Month' | 'Year'>('Week');
const [buyListingsLoading, setBuyListingsLoading] = useState(false);

  // Fetch all data
const fetchAllData = async () => {
  try {
    setLoading(true);
    setError(null);

    const [subData, plansData, billingData, extensionsData, listingUsageData] = await Promise.all([
      getCurrentSubscription(),
      getSubscriptionPlans(1),
      getBillingHistory({ limit: 10 }),
      getMyExtensionHistory(),
      getListingUsage(),                   // ✅ new
    ]);

    setSubscription(subData);

    const markedPlans = (plansData || []).map(plan => ({
      ...plan,
      IsPopular: plan.SubscriptionName?.toLowerCase().includes('pro') || false,
    }));
    setPlans(markedPlans);

    setBilling(billingData.data || []);
    setExtensions(extensionsData?.data || []);
    setListingUsage(listingUsageData?.data || null);   // ✅ new
  } catch (err: any) {
    setError(err.message || t('subscription.errors.loadFailed'));
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchAllData();
  }, []);

  // Handlers
  const handleUpgrade = async () => {
  if (!selectedPlanId) return;
  try {
    setActionLoading(true);
    setError(null);

    const result = await upgradeSubscription(selectedPlanId);

    if (!result.success) {
      setError(result.message || t('subscription.errors.upgradeFailed'));
      setActionLoading(false);
      return;
    }

    sessionStorage.setItem(
      "payzone_return_ctx",
      JSON.stringify({
        returnTo: "/dashboard/subscription",
        orderId: result.data.orderId,
      })
    );

    submitPaywallForm(
      result.data.paywallUrl,
      result.data.payloadJson,
      result.data.signature
    );
    // Do NOT setActionLoading(false) — navigating to Payzone
  } catch (err: any) {
    setError(err.message || t('subscription.errors.upgradeFailed'));
    setActionLoading(false);
  }
};
  const handlePurchase = async () => {
  if (!selectedPlanId) return;
  try {
    setActionLoading(true);
    setError(null);

    // Backend creates a Payzone payment session — subscription is NOT
    // created here. It's created by the Payzone callback after payment.
    const res = await purchaseSubscription(selectedPlanId);

    if (!res.success) {
      setError(res.message || "Failed to initiate payment");
      setActionLoading(false);
      return;
    }

    // Save context so we can return here after Payzone redirects back
    sessionStorage.setItem(
      "payzone_return_ctx",
      JSON.stringify({
        returnTo: "/dashboard/subscription",
        orderId: res.data.orderId,
      })
    );

    // Redirect browser to Payzone — page navigates away.
    // Do NOT setActionLoading(false) here.
    submitPaywallForm(
      res.data.paywallUrl,
      res.data.payloadJson,
      res.data.signature
    );
  } catch (err: any) {
    setError(
      err?.response?.data?.message ||
        "Failed to initiate payment. Please try again."
    );
    setActionLoading(false);
  }
};

const handleRenew = async () => {
  try {
    setActionLoading(true);
    setError(null);

    const result = await renewSubscription();

    if (!result.success) {
      setError(result.message || t('subscription.errors.renewalFailed'));
      setActionLoading(false);
      return;
    }

    sessionStorage.setItem(
      "payzone_return_ctx",
      JSON.stringify({
        returnTo: "/dashboard/subscription",
        orderId: result.data.orderId,
      })
    );

    submitPaywallForm(
      result.data.paywallUrl,
      result.data.payloadJson,
      result.data.signature
    );
  } catch (err: any) {
    setError(err.message || t('subscription.errors.renewalFailed'));
    setActionLoading(false);
  }
};
// ✅ Additional listings purchase
const handleBuyListings = async () => {
  if (!listingUsage) return;

  const selected = listingUsage.availableOptions.find(
    (o) => o.duration === buyListingsDuration
  );
  if (!selected) {
    setError('Selected duration is not available right now.');
    return;
  }
  if (buyListingsQty < 1 || buyListingsQty > 100) {
    setError('Quantity must be between 1 and 100.');
    return;
  }

  try {
    setBuyListingsLoading(true);
    setError(null);

    const res = await initiateAdditionalListingPurchase({
      quantity: buyListingsQty,
      duration: buyListingsDuration,
    });

    if (!res.success) {
      setError(res.message || 'Failed to initiate payment');
      setBuyListingsLoading(false);
      return;
    }

    // Save return context
    sessionStorage.setItem(
      'payzone_return_ctx',
      JSON.stringify({
        returnTo: '/dashboard/subscription',
        orderId: res.data.orderId,
      })
    );

    // Redirect to Payzone
    submitPaywallForm(
      res.data.paywallUrl,
      res.data.payloadJson,
      res.data.signature
    );
    // Do NOT setBuyListingsLoading(false) — navigating away
  } catch (err: any) {
    setError(
      err?.response?.data?.message ||
        'Failed to initiate payment. Please try again.'
    );
    setBuyListingsLoading(false);
  }
};

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-[#22C55E]/10 text-[#22C55E]';
      case 'pending':
        return 'bg-yellow-50 text-yellow-600';
      case 'failed':
        return 'bg-red-50 text-red-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      paid: t('status.paid'),
      pending: t('status.pending'),
      failed: t('status.failed'),
    };
    return map[status?.toLowerCase()] || status;
  };

  if (loading) {
    return <SubscriptionSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">{t('subscription.errors.loadFailed')}</p>
            <p className="text-sm">{error}</p>
            <button 
              onClick={fetchAllData}
              className="mt-2 text-sm text-red-700 underline hover:no-underline"
            >
              {t('subscription.errors.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No active subscription
  if (!subscription) {
    return (
      <div className="p-4 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl text-[#2D323A]">{t('title')}</h1>
          <p className="text-[#717182]">{t('subscription.noSubscription.getStarted')}</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#1E6DFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#1E6DFF]" />
            </div>
            <h3 className="text-lg font-medium text-[#2D323A] mb-2">{t('subscription.noSubscription.title')}</h3>
            <p className="text-sm text-[#717182]">{t('subscription.noSubscription.description')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {plans.map((plan) => (
              <label
                key={plan.id}
                className={`p-4 border-2 rounded-2xl cursor-pointer transition-all ${
                  selectedPlanId === plan.id
                    ? 'border-[#1E6DFF] bg-[#1E6DFF]/5'
                    : 'border-[#E5E7EB] hover:border-[#BFC2C7]'
                }`}
              >
                <input
                  type="radio"
                  name="plan"
                  className="hidden"
                  value={plan.id}
                  onChange={() => setSelectedPlanId(plan.id)}
                />
                <div className="flex items-start justify-between mb-2">
                  <span className="text-[#2D323A] font-medium">{plan.SubscriptionName}</span>
                  {plan.IsPopular && (
                    <span className="text-xs px-2 py-0.5 bg-[#1E6DFF] text-white rounded-lg">{t('subscription.upgradeModal.popular')}</span>
                  )}
                </div>
                <p className="text-2xl text-[#1E6DFF] mb-2">
                  {plan.Price === 0 ? t('free') : `${plan.Price} MAD`}
                </p>
                <p className="text-xs text-[#717182]">
                  {plan.NumberOfListings === -1 ? t('subscription.upgradeModal.unlimited') : plan.NumberOfListings} listings
                </p>
                <p className="text-xs text-[#717182]">
                  {plan.ValidityValue} {plan.ValidityType}(s)
                </p>
              </label>
            ))}
          </div>

          <button
            onClick={handlePurchase}
            disabled={!selectedPlanId || actionLoading}
            className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading 
              ? t('subscription.currentPlan.processing')
              : selectedPlanId 
                ? t('subscription.upgradeModal.subscribeTo', { plan: plans.find(p => p.id === selectedPlanId)?.SubscriptionName })
                : t('subscription.upgradeModal.selectPlan')
            }
          </button>
        </div>
      </div>
    );
  }

  // Active subscription UI
  const {
    planName,
    status,
    usedListings,
    totalListings,
    price,
    daysLeft,
    featuredUsed = 0,
    featuredTotal = 0,
  } = subscription;

  const usagePercent = totalListings > 0 ? (usedListings / totalListings) * 100 : 0;
  const featuredPercent = featuredTotal > 0 ? (featuredUsed / featuredTotal) * 100 : 0;

  return (
    <>
    <Seo
      title="Subscription & Billing | Morrocars"
      description="Manage your Morrocars subscription, view your current plan, track listing usage, upgrade or renew your subscription, and view billing history."
    />
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl text-[#2D323A]">{t('subscription.title')}</h1>
        <p className="text-[#717182]">{t('subscription.subtitle')}</p>
      </div>

      {/* Current Plan Card */}
      <div className="bg-gradient-to-br from-[#1E6DFF] to-[#1557d9] rounded-2xl p-6 text-white mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-white/70 text-sm mb-1">{t('subscription.currentPlan.title')}</p>
            <h2 className="text-3xl">{planName}</h2>
          </div>
          <span className="px-3 py-1.5 bg-white/20 backdrop-blur rounded-xl text-sm flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> {status}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl">{usedListings}/{totalListings === -1 ? t('subscription.usage.unlimited') : totalListings}</p>
            <p className="text-sm text-white/70">{t('subscription.currentPlan.listingsUsed')}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
  <p className="text-2xl">{price} MAD</p>
  <p className="text-sm text-white/70">
    {subscription.validityValue ?? 1}{" "}
    {(subscription.validityType ?? 'month').toLowerCase()}
    {(subscription.validityValue ?? 1) > 1 ? 's' : ''}
  </p>
</div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl">{daysLeft > 0 ? daysLeft : 0}</p>
            <p className="text-sm text-white/70">{daysLeft > 0 ? t('subscription.currentPlan.daysLeft') : t('subscription.currentPlan.expired')}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setUpgrading(true)}
            disabled={actionLoading}
            className="flex-1 py-2.5 bg-white text-[#1E6DFF] rounded-xl hover:bg-white/90 transition-colors text-sm flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUpRight className="w-4 h-4" /> {t('subscription.currentPlan.upgrade')}
          </button>
          <button
            onClick={handleRenew}
            disabled={actionLoading}
            className="flex-1 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? t('subscription.currentPlan.processing') : t('subscription.currentPlan.renew')}
          </button>
        </div>
      </div>

      {/* Usage */}
      {/* <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-6">
        <h3 className="text-[#2D323A] mb-4">{t('subscription.usage.title')}</h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-[#717182]">{t('subscription.usage.activeListings')}</span>
              <span className="text-sm text-[#2D323A]">{usedListings} / {totalListings === -1 ? t('subscription.usage.unlimited') : totalListings}</span>
            </div>
            <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1E6DFF] rounded-full transition-all duration-500"
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
          </div>
          {featuredTotal > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-[#717182]">{t('subscription.usage.featuredListings')}</span>
                <span className="text-sm text-[#2D323A]">{featuredUsed} / {featuredTotal}</span>
              </div>
              <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#22C55E] rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(featuredPercent, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div> */}
      {/* ✅ Usage — enriched with extra slots */}
{listingUsage && (
  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-[#2D323A]">{t('subscription.usage.title')}</h3>
      {!listingUsage.isUnlimited && (
        <button
          onClick={() => {
            // Pre-select first available duration
            const first = listingUsage.availableOptions[0]?.duration;
            if (first) setBuyListingsDuration(first);
            setBuyListingsQty(1);
            setShowBuyListingsModal(true);
          }}
          disabled={listingUsage.availableOptions.length === 0}
          className="text-sm px-3 py-1.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Buy More Listings
        </button>
      )}
    </div>

    {/* Slots breakdown */}
    <div className="grid grid-cols-3 gap-3 mb-4">
      <div className="bg-[#F5F7FA] rounded-xl p-3 text-center">
        <p className="text-2xl text-[#2D323A]">
          {listingUsage.isUnlimited ? '∞' : listingUsage.baseSlots}
        </p>
        <p className="text-xs text-[#717182] mt-1">Base slots</p>
      </div>
      <div className="bg-[#1E6DFF]/10 rounded-xl p-3 text-center">
        <p className="text-2xl text-[#1E6DFF]">+{listingUsage.extraSlots}</p>
        <p className="text-xs text-[#717182] mt-1">Extra slots</p>
      </div>
      <div className="bg-[#F5F7FA] rounded-xl p-3 text-center">
        <p className="text-2xl text-[#2D323A]">
          {listingUsage.usedSlots} /{' '}
          {listingUsage.isUnlimited ? '∞' : listingUsage.totalAllowed}
        </p>
        <p className="text-xs text-[#717182] mt-1">Used</p>
      </div>
    </div>

    {/* Progress bar */}
    <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          !listingUsage.isUnlimited &&
          listingUsage.usedSlots >= listingUsage.totalAllowed
            ? 'bg-red-500'
            : 'bg-[#1E6DFF]'
        }`}
        style={{
          width: `${
            listingUsage.isUnlimited
              ? 0
              : Math.min(
                  100,
                  (listingUsage.usedSlots / listingUsage.totalAllowed) * 100
                )
          }%`,
        }}
      />
    </div>

    {/* Limit warning */}
    {!listingUsage.isUnlimited &&
      listingUsage.usedSlots >= listingUsage.totalAllowed && (
        <div className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            You have used all {listingUsage.totalAllowed} listing slots. Buy more to add new vehicles.
          </span>
        </div>
      )}

    {/* Active extra purchases */}
    {listingUsage.activePurchases.length > 0 && (
      <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
        <p className="text-xs text-[#717182] mb-2 font-medium">Active extra purchases</p>
        <div className="space-y-1">
          {listingUsage.activePurchases.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between text-xs text-[#717182]"
            >
              <span>
                +{p.quantity} {p.duration.toLowerCase()}
                {p.quantity > 1 ? 's' : ''}
              </span>
              <span>
                expires{' '}
                {new Date(p.expiresOn).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Featured bar (unchanged from original) */}
    {featuredTotal > 0 && (
      <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm text-[#717182]">
            {t('subscription.usage.featuredListings')}
          </span>
          <span className="text-sm text-[#2D323A]">
            {featuredUsed} / {featuredTotal}
          </span>
        </div>
        <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#22C55E] rounded-full transition-all duration-500"
            style={{ width: `${Math.min(featuredPercent, 100)}%` }}
          />
        </div>
      </div>
    )}
  </div>
)}

      {/* Upgrade Modal */}
      {upgrading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 p-5 border-b border-[#E5E7EB] flex items-center justify-between">
              <h3 className="text-[#2D323A] text-lg font-medium">{t('subscription.upgradeModal.title')}</h3>
              <button
                onClick={() => {
                  setUpgrading(false);
                  setSelectedPlanId("");
                }}
                className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans
                  .filter(p => p.id !== subscription.subscriptionPlanId)
                  .map((plan) => (
                    <label
                      key={plan.id}
                      className={`p-4 border-2 rounded-2xl cursor-pointer transition-all ${
                        selectedPlanId === plan.id
                          ? 'border-[#1E6DFF] bg-[#1E6DFF]/5'
                          : 'border-[#E5E7EB] hover:border-[#BFC2C7]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="upgradePlan"
                        className="hidden"
                        value={plan.id}
                        onChange={() => setSelectedPlanId(plan.id)}
                      />
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-[#2D323A] font-medium">{plan.SubscriptionName}</span>
                        {plan.IsPopular && (
                          <span className="text-xs px-2 py-0.5 bg-[#1E6DFF] text-white rounded-lg">{t('subscription.upgradeModal.popular')}</span>
                        )}
                      </div>
                      <p className="text-2xl text-[#1E6DFF] mb-2">
                        {plan.Price === 0 ? t('free') : `${plan.Price} MAD`}
                      </p>
                      <ul className="space-y-1">
                        <li className="text-xs text-[#717182] flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-[#22C55E]" />
                          {plan.NumberOfListings === -1 ? t('subscription.upgradeModal.unlimited') : plan.NumberOfListings} listings
                        </li>
                        <li className="text-xs text-[#717182] flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-[#22C55E]" />
                          {plan.ValidityValue} {plan.ValidityType}(s)
                        </li>
                      </ul>
                    </label>
                  ))}
              </div>

              <button
                onClick={handleUpgrade}
                disabled={!selectedPlanId || actionLoading}
                className="mt-6 w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading 
                  ? t('subscription.currentPlan.processing')
                  : selectedPlanId 
                    ? t('subscription.upgradeModal.upgradeTo', { 
                        plan: plans.find(p => p.id === selectedPlanId)?.SubscriptionName,
                        price: plans.find(p => p.id === selectedPlanId)?.Price
                      })
                    : t('subscription.upgradeModal.selectPlan')
                }
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ✅ Buy Listings Modal */}
{showBuyListingsModal && listingUsage && (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 p-5 border-b border-[#E5E7EB] flex items-center justify-between">
        <div>
          <h3 className="text-[#2D323A] text-lg font-medium">Buy Additional Listings</h3>
          <p className="text-xs text-[#717182] mt-0.5">
            Slots expire on the earlier of the chosen duration or your subscription's end date
          </p>
        </div>
        <button
          onClick={() => {
            if (buyListingsLoading) return;
            setShowBuyListingsModal(false);
          }}
          disabled={buyListingsLoading}
          className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* No options available */}
        {listingUsage.availableOptions.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              Additional listings are not available right now for your plan.
              Please try again on the first day of your subscription period.
            </div>
          </div>
        ) : (
          <>
            {/* Duration picker */}
            <div>
              <label className="block text-sm mb-2 text-[#2D323A] font-medium">
                Choose duration
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {listingUsage.availableOptions.map((opt) => {
                  const isSelected = buyListingsDuration === opt.duration;
                  return (
                    <button
                      key={opt.duration}
                      type="button"
                      onClick={() => {
                        if (buyListingsLoading) return;
                        setBuyListingsDuration(opt.duration);
                      }}
                      disabled={buyListingsLoading}
                      className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                        isSelected
                          ? 'border-[#1E6DFF] bg-[#1E6DFF]/5'
                          : 'border-[#E5E7EB] hover:border-[#BFC2C7]'
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      {opt.willBeProrated && (
                        <span className="absolute -top-2 right-2 text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">
                          Prorated
                        </span>
                      )}
                      <p className="text-sm font-medium text-[#2D323A]">{opt.duration}</p>
                      <div className="mt-2">
                        {opt.willBeProrated ? (
                          <>
                            <p className="text-xs text-[#717182] line-through">
                              {opt.unitPriceFull} MAD
                            </p>
                            <p className="text-lg text-[#1E6DFF] font-semibold">
                              {opt.unitPriceActual} MAD
                            </p>
                          </>
                        ) : (
                          <p className="text-lg text-[#1E6DFF] font-semibold">
                            {opt.unitPriceActual} MAD
                          </p>
                        )}
                        <p className="text-xs text-[#717182] mt-1">
                          per listing · {opt.servedDays} day
                          {opt.servedDays > 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {listingUsage.availableOptions.find((o) => o.duration === buyListingsDuration)?.willBeProrated && (
                <p className="text-xs text-orange-700 bg-orange-50 rounded-lg p-2 mt-2">
                  This option will end when your current subscription expires. You'll be charged only for the days remaining.
                </p>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm mb-2 text-[#2D323A] font-medium">
                Number of listings
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBuyListingsQty((q) => Math.max(1, q - 1))}
                  disabled={buyListingsLoading || buyListingsQty <= 1}
                  className="w-10 h-10 rounded-xl border border-[#E5E7EB] flex items-center justify-center text-[#2D323A] hover:bg-[#F5F7FA] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={buyListingsQty}
                  onChange={(e) => {
                    const v = Math.floor(Number(e.target.value));
                    if (Number.isFinite(v) && v >= 1 && v <= 100) {
                      setBuyListingsQty(v);
                    }
                  }}
                  disabled={buyListingsLoading}
                  className="flex-1 h-10 px-3 rounded-xl border border-[#E5E7EB] text-center outline-none focus:border-[#1E6DFF] disabled:bg-[#F5F7FA]"
                />
                <button
                  type="button"
                  onClick={() => setBuyListingsQty((q) => Math.min(100, q + 1))}
                  disabled={buyListingsLoading || buyListingsQty >= 100}
                  className="w-10 h-10 rounded-xl border border-[#E5E7EB] flex items-center justify-center text-[#2D323A] hover:bg-[#F5F7FA] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            {/* Total */}
            <div className="bg-[#F5F7FA] rounded-xl p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#717182]">
                  {buyListingsQty} ×{' '}
                  {listingUsage.availableOptions.find((o) => o.duration === buyListingsDuration)?.unitPriceActual || 0} MAD
                </span>
                <span className="text-lg font-semibold text-[#2D323A]">
                  {(
                    buyListingsQty *
                    (listingUsage.availableOptions.find((o) => o.duration === buyListingsDuration)?.unitPriceActual || 0)
                  ).toFixed(2)}{' '}
                  MAD
                </span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Pay */}
            <button
              onClick={handleBuyListings}
              disabled={buyListingsLoading || buyListingsQty < 1}
              className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {buyListingsLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting to payment...
                </>
              ) : (
                `Pay ${(
                  buyListingsQty *
                  (listingUsage.availableOptions.find((o) => o.duration === buyListingsDuration)?.unitPriceActual || 0)
                ).toFixed(2)} MAD`
              )}
            </button>
          </>
        )}
      </div>
    </div>
  </div>
)}

      {/* Billing History */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB]">
        <div className="p-5 border-b border-[#E5E7EB]">
          <h3 className="text-[#2D323A]">{t('subscription.billing.title')}</h3>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {billing.length === 0 ? (
            <div className="px-5 py-8 text-center text-[#717182] text-sm">
              {t('subscription.billing.noHistory')}
            </div>
          ) : (
            billing.map((b, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4 hover:bg-[#F9FAFB] transition-colors">
                <div className="w-10 h-10 bg-[#22C55E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-[#22C55E]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#2D323A] font-medium">{b.plan} {t('subscription.billing.monthly')}</p>
                  <p className="text-xs text-[#717182]">{b.date}</p>
                </div>
                <span className="text-sm text-[#2D323A] font-medium whitespace-nowrap">{b.amount} MAD</span>
                <span className={`text-xs px-2 py-0.5 rounded-lg whitespace-nowrap ${getStatusColor(b.status)}`}>
                  {getStatusLabel(b.status)}
                </span>
                <button className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182] transition-colors flex-shrink-0">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      {/* ✅ Extension History — only when there are records */}
{extensions.length > 0 && (
  <div className="bg-white rounded-2xl border border-[#E5E7EB] mb-6">
    <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-2">
      <ArrowUpRight className="w-5 h-5 text-[#1E6DFF]" />
      <h3 className="text-[#2D323A]">Subscription Extension History</h3>
    </div>

    <div className="divide-y divide-[#E5E7EB]">
      {extensions.map((ext) => (
        <div
          key={ext.id}
          className="flex items-start gap-4 px-5 py-4 hover:bg-[#F9FAFB] transition-colors"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#1E6DFF]/10">
            <ArrowUpRight className="w-5 h-5 text-[#1E6DFF]" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-[#2D323A] font-medium">
                {ext.subscriptionName}
              </p>
              <span
                className={`text-xs px-2 py-0.5 rounded-lg ${
                  ext.operationType === 'NEW'
                    ? 'bg-orange-50 text-orange-700'
                    : 'bg-blue-50 text-blue-700'
                }`}
              >
                {ext.operationType === 'NEW' ? 'New Subscription' : 'Extended'}
              </span>
            </div>

            <p className="text-xs text-[#717182] mt-1">
              {ext.operationType === 'NEW' ? 'Valid from' : 'Extended from'}{' '}
              {new Date(ext.previousEndDate).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}{' '}
              → {new Date(ext.newEndDate).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </p>

            {ext.notes && (
              <p className="text-xs text-[#717182] mt-1 italic">
                "{ext.notes}"
              </p>
            )}
          </div>

          <div className="text-right flex-shrink-0">
            <p className="text-sm text-[#1E6DFF] font-medium">
              +{ext.monthsExtended} {ext.monthsExtended === 1 ? 'month' : 'months'}
            </p>
            <p className="text-xs text-[#717182] mt-0.5">
              {new Date(ext.createdOn).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
    </div>
    </>
  );
}